// Unified Receipt Formatter Service
// Formats receipts and order slips for different printer types: browser, QZ Tray, and RawBT
import { APP_NAME, APP_DESCRIPTION } from "../constants/app";

// Debug helper function for logging to server console
const debugLog = async (message, data = null, level = 'info') => {
  try {
    const response = await fetch('/api/v1/test/debug-log', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({
        message,
        data,
        level,
        timestamp: new Date().toISOString()
      })
    });
    
    if (!response.ok) {
      console.warn('Failed to send debug log to server:', response.statusText);
    }
  } catch (error) {
    console.warn('Error sending debug log to server:', error.message);
  }
};

// Specialized debug function for receipt formatter
const debugReceiptLog = async (step, receiptData, normalizedData, printerType, serviceChargeInfo, branchTaken) => {
  try {
    const response = await fetch('/api/v1/test/receipt-debug', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({
        step,
        receiptData,
        normalizedData,
        printerType,
        serviceChargeInfo,
        branchTaken
      })
    });
    
    if (!response.ok) {
      console.warn('Failed to send receipt debug log to server:', response.statusText);
    }
  } catch (error) {
    console.warn('Error sending receipt debug log to server:', error.message);
  }
};

export class ReceiptFormatter {
  constructor(printerType = 'browser', thermalWidth = 58) {
    this.printerType = printerType; // 'browser', 'qz_tray', 'rawbt'
    this.thermalWidth = thermalWidth; // 58mm or 80mm
    this.charWidth = this.getCharWidth();
  }

  // Calculate character width based on thermal printer width
  getCharWidth() {
    switch (this.thermalWidth) {
      case 58:
        return 32; // 58mm = ~32 characters (condensed font)
      case 80:
        return 46; // 80mm = ~46 characters (condensed font)
      default:
        return 32;
    }
  }

  // Format currency consistently
  formatCurrency(amount) {
    return `${parseFloat(amount || 0).toFixed(2)}`;
  }

  // Format date/time consistently
  formatDateTime(dateString) {
    if (!dateString) return "";
    return new Date(dateString).toLocaleString("en-PH", {
      year: "numeric",
      month: "short",
      day: "numeric",
      hour: "2-digit",
      minute: "2-digit",
      hour12: true,
    });
  }

  // Text formatting utilities
  centerText(text, width = this.charWidth) {
    if (text.length >= width) return text;
    const padding = Math.max(0, Math.floor((width - text.length) / 2));
    return (
      " ".repeat(padding) + text + " ".repeat(width - text.length - padding)
    );
  }

  rightAlign(text, width = this.charWidth) {
    const padding = Math.max(0, width - text.length);
    return " ".repeat(padding) + text;
  }

  formatLine(left, right, width = this.charWidth) {
    const availableSpace = width - right.length;
    const leftTruncated =
      left.length > availableSpace
        ? left.substring(0, availableSpace - 3) + "..."
        : left;
    const padding = Math.max(0, availableSpace - leftTruncated.length);
    return leftTruncated + " ".repeat(padding) + right;
  }

  // Normalize receipt data from different sources (POS vs Transactions)
  normalizeReceiptData(receiptData) {
    //normalize service_charge_inclusive field
    // receiptData.transaction.service_charge_inclusive = Boolean(receiptData.transaction.service_charge_inclusive);
    const val = receiptData.service_charge_inclusive;
    receiptData.service_charge_inclusive = ['true', '1', true, 1].includes(val);

    // Check if this is the structure from ThermalReceiptPreview (transaction + transactionDetails)
    if (receiptData.transaction && receiptData.transactionDetails) {
      return {
        // Basic transaction info
        txn_no: receiptData.transaction.txn_no,
        date_created: receiptData.transaction.date_created,
        cashier_name: receiptData.transaction.cashier_name,
        customer: receiptData.transaction.customer,
        
        // Financial data
        subtotal: receiptData.transaction.subtotal,
        total_discounts: receiptData.transaction.total_discounts || 0,
        service_charge_amount: receiptData.transaction.service_charge_amount || 0,
        service_charge_inclusive: receiptData.transaction.service_charge_inclusive,
        grand_total: receiptData.transaction.grand_total,
        payment_total: receiptData.transaction.payment_total,
        change: receiptData.transaction.change || 0,
        
        // Items from transactionDetails
        items: receiptData.transactionDetails.details || [],
        
        // Payments
        payments: receiptData.transactionDetails.payments || []
      };
    }
    
    // Check if this is POS notification data structure
    if (receiptData.transaction && receiptData.cart && receiptData.subtotal !== undefined) {
      
      // POS structure - convert to unified format
      const posResult = {
        // Basic transaction info
        txn_no: receiptData.transaction.txn_no,
        date_created: receiptData.transaction.date_created,
        cashier_name: receiptData.transaction.cashier_name,
        customer: receiptData.customer,
        
        // Financial data
        subtotal: receiptData.subtotal,
        total_discounts: receiptData.totalDiscount || 0,
        service_charge_amount: receiptData.serviceChargeAmount || 0,
        service_charge_inclusive: receiptData.transaction.service_charge_inclusive || false,
        grand_total: receiptData.transaction.grand_total,
        payment_total: receiptData.transaction.payment_total,
        change: receiptData.transaction.change, // POS doesn't track change, it's exact payment
        
        // Items from cart
        items: receiptData.cart.map(item => ({
          product: { name: item.product_name || item.name },
          qty: item.qty || item.quantity,
          unit_price: item.price || item.unit_price,
          total: (item.price || item.unit_price) * (item.qty || item.quantity),
          note: item.isComplimentary ? 'COMPLIMENTARY' : (item.note || ''),
          isComplimentary: item.isComplimentary || false
        })),
        
        // Payments
        payments: receiptData.payments || [{ payment_type: 'CASH' }]
      };
      
      return posResult;
    }

    // Assume it's already in the correct format
    
    return {
      ...receiptData,
      items: receiptData.items || receiptData.transactionDetails?.details || []
    };
  }

  // Generate receipt content based on printer type
  generateReceipt(receiptData) {
    debugLog(`Receipt Data: `, receiptData);
    const normalizedData = this.normalizeReceiptData(receiptData);
    debugLog(`Normalized Data: `, normalizedData);
    
    const businessName = JSON.parse(localStorage.getItem('user'))?.entity_name || APP_NAME;

    debugLog(`Printer Type: ${this.printerType}`, null);
    switch (this.printerType) {
      case 'browser':
        return this.generateBrowserReceipt(normalizedData, businessName);
      case 'qz_tray':
      case 'rawbt':
        return this.generateThermalReceipt(normalizedData, businessName);
      default:
        return this.generateBrowserReceipt(normalizedData, businessName);
    }
  }

  // Generate receipt content for browser printing (plain text)
  generateBrowserReceipt(data, businessName) {

    // debugLog('🖨️ generateBrowserReceipt called', {
    //   service_charge_amount: data.service_charge_amount,
    //   service_charge_inclusive: data.service_charge_inclusive,
    //   serviceCharge: data.serviceCharge,
    //   serviceChargeInclusive: data.serviceChargeInclusive,
    //   willShowInclusiveLabel: data.service_charge_inclusive === true && data.service_charge_amount > 0
    // });

    const lineBreak = "=".repeat(this.charWidth);

    let content = `${this.centerText(businessName.toUpperCase())}
${this.centerText("Point of Sale System")}
${this.centerText(APP_DESCRIPTION)}
${lineBreak}
${this.formatLine("Date:", this.formatDateTime(data.date_created))}
${this.formatLine("TXN #:", data.txn_no || "N/A")}
${this.formatLine("Cashier:", data.cashier_name || "N/A")}
${this.formatLine("Customer:", data.customer?.display_name || "Walk-in")}
${lineBreak}
ITEMS:
`;

    // Add items
    if (data.items && data.items.length > 0) {
      data.items.forEach((item) => {
        const isComplimentary = item.note === 'COMPLIMENTARY' || item.isComplimentary;
        const itemName = item.product.name || "Unknown Item";
        const itemNameWithTag = itemName + (isComplimentary ? ' [COMP]' : '');
        const qty = `${item.qty}x`;
        const actualPrice = isComplimentary ? 0 : item.unit_price;
        const price = this.formatCurrency(actualPrice);
        const total = this.formatCurrency(item.qty * actualPrice);

        content += `${this.formatLine(itemNameWithTag, "")}\n`;
        content += `${this.formatLine(`  ${qty} @ ${price}`, total)}\n`;
      });
    } else {
      content += "No items\n";
    }

    content += `${lineBreak}
${this.formatLine("Subtotal:", this.formatCurrency(data.subtotal))}`;

    // Add discounts if any
    if (data.total_discounts > 0) {
      content += `\n${this.formatLine("Discount:", `-${this.formatCurrency(data.total_discounts)}`)}`;
    }

    // Add service charge if not inclusive
    if (data.service_charge_amount > 0 && (data.service_charge_inclusive === null || !data.service_charge_inclusive)) {
      content += `\n${this.formatLine("Service Charge:", this.formatCurrency(data.service_charge_amount))}`;
    }

    // Add tax if applicable
    if (data.grand_total * (0.12 / 1.12) > 0) {
      content += `\n${this.formatLine("Tax:", this.formatCurrency(data.grand_total * (0.12 / 1.12)))}`;
    }

    content += `\n${this.formatLine("TOTAL:", this.formatCurrency(data.grand_total))}`;

    // Add service charge inclusive note
    if (data.service_charge_inclusive === true && data.service_charge_amount > 0) {
      content += `\n${this.centerText("(Inclusive of service charge)")}`;
    }

    content += `\n${lineBreak}
${this.formatLine(
  "Payment:",
  data.payments?.length < 2
    ? data.payments[0]?.payment_type || "N/A"
    : "MIXED"
)}
${this.formatLine("Amount Paid:", this.formatCurrency(data.payment_total))}`;

    // Add change if any
    if (data.change > 0) {
      content += `\n${this.formatLine("Change:", this.formatCurrency(data.change))}`;
    }

    content += `\n${lineBreak}
${this.centerText("Thank you for your purchase!")}
${this.centerText("Please come again!")}
${lineBreak}
${this.centerText("Receipt generated on:")}
${this.centerText(
  new Date().toLocaleString("en-PH", {
    month: "numeric",
    day: "numeric",
    year: "numeric",
    hour: "numeric",
    minute: "2-digit",
    hour12: true,
  })
)}

${this.centerText("Powered by TiendaSuite POS")}
${this.centerText("http://tiendasuite.app")}
${lineBreak}`;

    return content;
  }

  // Generate receipt content for thermal printers (ESC/POS commands)
  generateThermalReceipt(data, businessName) {
    // Debug log the exact data received
    // debugLog('🖨️ generateThermalReceipt called', {
    //   service_charge_amount: data.service_charge_amount,
    //   service_charge_inclusive: data.service_charge_inclusive,
    //   serviceCharge: data.serviceCharge,
    //   serviceChargeInclusive: data.serviceChargeInclusive,
    //   willShowInclusiveLabel: data.service_charge_inclusive === true && data.service_charge_amount > 0
    // });
    
    let formatted = '';
    
    // ESC/POS commands for thermal printing
    const ESC = String.fromCharCode(27);
    const GS = String.fromCharCode(29);
    const LF = String.fromCharCode(10); // Line feed
    
    // Initialize printer
    formatted += ESC + '@'; // Initialize printer
    
    // Header - centered
    formatted += ESC + 'a' + String.fromCharCode(1); // Center align
    formatted += businessName.toUpperCase() + LF;
    formatted += 'Point of Sale System' + LF;
    // formatted += APP_DESCRIPTION + LF;
    
    // Line break
    formatted += ESC + 'a' + String.fromCharCode(0); // Left align
    formatted += '='.repeat(this.charWidth) + LF;
    
    // Transaction details
    formatted += this.formatLine('Date:', this.formatDateTime(data.date_created)) + LF;
    formatted += this.formatLine('TXN #:', data.txn_no || 'N/A') + LF;
    formatted += this.formatLine('Cashier:', data.cashier_name || 'N/A') + LF;
    formatted += this.formatLine('Customer:', data.customer?.display_name || 'Walk-in') + LF;
    formatted += '='.repeat(this.charWidth) + LF;
    
    // Items
    formatted += 'ITEMS:' + LF;
    if (data.items && data.items.length > 0) {
      data.items.forEach(item => {
        const itemName = item.product.name || 'Unknown Item';
        const qty = `${item.qty}x`;
        const isComplimentary = item.note === 'COMPLIMENTARY';
        // For complimentary items, use 0 for price calculations
        const actualPrice = isComplimentary ? 0 : item.unit_price;
        const price = this.formatCurrency(actualPrice);
        const total = this.formatCurrency(item.qty * actualPrice);
        
        // Item name on its own line (with COMPLIMENTARY tag if applicable)
        formatted += itemName + (isComplimentary ? ' [COMP]' : '') + LF;
        // Quantity and price aligned right
        formatted += this.formatLine(`  ${qty} @ ${price}`, total) + LF;
      });
    } else {
      formatted += 'No items' + LF;
    }
    
    formatted += '='.repeat(this.charWidth) + LF;
    
    // Financial totals
    formatted += this.formatLine('Subtotal:', this.formatCurrency(data.subtotal)) + LF;
    
    // Discounts
    if (data.total_discounts > 0) {
      formatted += this.formatLine('Discount:', `-${this.formatCurrency(data.total_discounts)}`) + LF;
    }
    
    // Service charge (only show if NOT inclusive)
    if (data.service_charge_amount > 0 && (data.service_charge_inclusive === null || !data.service_charge_inclusive)) {
      formatted += this.formatLine('Service Charge:', this.formatCurrency(data.service_charge_amount)) + LF;
    }
    
    // Tax calculation
    if (data.grand_total * (0.12 / 1.12) > 0) {
      formatted += this.formatLine('Tax:', this.formatCurrency(data.grand_total * (0.12 / 1.12))) + LF;
    }
    
    // Grand total
    formatted += this.formatLine('TOTAL:', this.formatCurrency(data.grand_total)) + LF;
    
    // Service charge inclusive note (centered)
    if (data.service_charge_inclusive === true && data.service_charge_amount > 0) {
      formatted += ESC + 'a' + String.fromCharCode(1); // Center align
      formatted += '(Inclusive of service charge)' + LF;
      formatted += ESC + 'a' + String.fromCharCode(0); // Left align
    }
    
    formatted += '='.repeat(this.charWidth) + LF;
    
    // Payment information
    const paymentType = (data.payments?.length === 1) ? 
      (data.payments[0].payment_type || 'CASH') : 'MIXED';
    formatted += this.formatLine('Payment:', paymentType) + LF;
    formatted += this.formatLine('Amount Paid:', this.formatCurrency(data.payment_total)) + LF;
    
    if (data.change && data.change > 0) {
      formatted += this.formatLine('Change:', this.formatCurrency(data.change)) + LF;
    }
    
    formatted += '='.repeat(this.charWidth) + LF;
    
    // Footer - centered
    formatted += ESC + 'a' + String.fromCharCode(1); // Center align
    formatted += 'Thank you for your purchase!' + LF;
    formatted += 'Please come again!' + LF;
    formatted += '='.repeat(this.charWidth) + LF;
    formatted += 'Receipt generated on:' + LF;
    formatted += new Date().toLocaleString("en-PH", {
      month: "numeric",
      day: "numeric", 
      year: "numeric",
      hour: "2-digit",
      minute: "2-digit",
      hour12: true,
    }) + LF;
    formatted += '='.repeat(this.charWidth) + LF;
    formatted += LF;
    formatted += 'Powered by TiendaSuite POS' + LF;
    formatted += 'http://tiendasuite.app' + LF;
    
    formatted += ESC + 'a' + String.fromCharCode(0); // Left align
    formatted += LF + LF + LF; // Feed paper
    
    // Printer-specific commands
    if (this.printerType === 'rawbt' || this.printerType === 'qz_tray') {
      // Open cash drawer command
      formatted += ESC + 'p' + String.fromCharCode(0) + String.fromCharCode(25) + String.fromCharCode(250);
      
      // Cut paper command
      formatted += GS + 'V' + String.fromCharCode(0);
    }
    
    return formatted;
  }

  // Normalize order slip data from different sources
  normalizeOrderSlipData(orderData) {
    // Handle different order data structures
    return {
      orderNumber: orderData.orderNumber || orderData.order_no || 'N/A',
      timestamp: orderData.timestamp || orderData.date_created || new Date().toISOString(),
      customer: orderData.customer || { display_name: 'Walk-in' },
      server: orderData.server || orderData.cashier_name || 'POS',
      items: orderData.items || orderData.cart || [],
      subtotal: orderData.subtotal || 0,
      totalDiscount: orderData.totalDiscount || orderData.total_discounts || 0,
      serviceChargeAmount: orderData.serviceChargeAmount || orderData.service_charge_amount || 0,
      tax: orderData.tax || 0,
      total: orderData.total || orderData.grand_total || 0,
      note: orderData.note || orderData.special_instructions || null
    };
  }

  // Generate order slip content based on printer type
  generateOrderSlip(orderData) {
    const normalizedData = this.normalizeOrderSlipData(orderData);
    const businessName = JSON.parse(localStorage.getItem('user'))?.entity_name || APP_NAME;

    switch (this.printerType) {
      case 'browser':
        return this.generateBrowserOrderSlip(normalizedData, businessName);
      case 'qz_tray':
      case 'rawbt':
        return this.generateThermalOrderSlip(normalizedData, businessName);
      default:
        return this.generateBrowserOrderSlip(normalizedData, businessName);
    }
  }

  // Generate order slip content for browser printing (plain text)
  generateBrowserOrderSlip(data, businessName) {
    const lineBreak = "=".repeat(this.charWidth);
    const dashBreak = "-".repeat(this.charWidth);

    let content = `${this.centerText("*** ORDER SLIP ***")}
${this.centerText(businessName.toUpperCase())}
${lineBreak}
${this.formatLine("Date:", this.formatDateTime(data.timestamp))}
${this.formatLine("Order #:", data.orderNumber)}
${this.formatLine("Table/Customer:", data.customer?.display_name || "Walk-in")}
${this.formatLine("Server:", data.server)}
${lineBreak}
ITEMS TO PREPARE:
${dashBreak}
`;

    // Add items
    if (data.items && data.items.length > 0) {
      data.items.forEach((item) => {
        const itemName = item.name || item.product?.name || "Unknown Item";
        const qty = `${item.qty || item.quantity || 1}x`;
        const price = this.formatCurrency(item.price || item.unit_price || 0);
        const total = this.formatCurrency((item.price || item.unit_price || 0) * (item.qty || item.quantity || 1));

        content += `${this.formatLine(itemName, "")}\n`;
        content += `${this.formatLine(`  ${qty} @ ${price}`, total)}`;
        
        if (item.note || item.special_instructions) {
          content += `\n  Note: ${item.note || item.special_instructions}`;
        }
        content += "\n";
      });
    } else {
      content += "No items\n";
    }

    content += `${dashBreak}
${this.formatLine("Subtotal:", this.formatCurrency(data.subtotal))}`;

    // Add discounts if any
    if (data.totalDiscount > 0) {
      content += `\n${this.formatLine("Discount:", `-${this.formatCurrency(data.totalDiscount)}`)}`;
    }

    // Add service charge if any
    if (data.serviceChargeAmount > 0) {
      content += `\n${this.formatLine("Service Charge:", this.formatCurrency(data.serviceChargeAmount))}`;
    }

    // Add tax if any
    if (data.tax > 0) {
      content += `\n${this.formatLine("Tax:", this.formatCurrency(data.tax))}`;
    }

    content += `\n${this.formatLine("TOTAL:", this.formatCurrency(data.total))}
${lineBreak}
Total Items: ${data.items?.length || 0}
Total Quantity: ${data.items?.reduce((sum, item) => sum + (item.qty || item.quantity || 1), 0) || 0}
${lineBreak}`;

    // Add special instructions if any
    if (data.note) {
      content += `Special Instructions:
${data.note}
${lineBreak}`;
    }

    content += `${this.centerText("*** END OF ORDER ***")}
${this.centerText(
  "Time: " +
    new Date().toLocaleTimeString("en-PH", {
      hour: "2-digit",
      minute: "2-digit",
      hour12: true,
    })
)}`;

    return content;
  }

  // Generate order slip content for thermal printers (ESC/POS commands)
  generateThermalOrderSlip(data, businessName) {
    let formatted = '';
    
    // ESC/POS commands for thermal printing
    const ESC = String.fromCharCode(27);
    const GS = String.fromCharCode(29);
    const LF = String.fromCharCode(10); // Line feed
    
    // Initialize printer
    formatted += ESC + '@'; // Initialize printer
    
    // Header - centered
    formatted += ESC + 'a' + String.fromCharCode(1); // Center align
    formatted += '*** ORDER SLIP ***' + LF;
    formatted += businessName.toUpperCase() + LF;
    
    // Line break
    formatted += ESC + 'a' + String.fromCharCode(0); // Left align
    formatted += '='.repeat(this.charWidth) + LF;
    
    // Order details
    formatted += this.formatLine('Date:', this.formatDateTime(data.timestamp)) + LF;
    formatted += this.formatLine('Order #:', data.orderNumber) + LF;
    formatted += this.formatLine('Table/Customer:', data.customer?.display_name || 'Walk-in') + LF;
    formatted += this.formatLine('Server:', data.server) + LF;
    formatted += '='.repeat(this.charWidth) + LF;
    
    // Items header
    formatted += 'ITEMS TO PREPARE:' + LF;
    formatted += '-'.repeat(this.charWidth) + LF;
    
    // Items
    if (data.items && data.items.length > 0) {
      data.items.forEach(item => {
        const itemName = item.name || item.product?.name || 'Unknown Item';
        const qty = `${item.qty || item.quantity || 1}x`;
        const isComplimentary = item.note === 'COMPLIMENTARY' || item.isComplimentary;
        // For complimentary items, use 0 for price calculations
        const actualPrice = isComplimentary ? 0 : (item.price || item.unit_price || 0);
        const price = this.formatCurrency(actualPrice);
        const total = this.formatCurrency(actualPrice * (item.qty || item.quantity || 1));
        
        // Item name on its own line (with COMPLIMENTARY tag if applicable)
        formatted += itemName + (isComplimentary ? ' [COMP]' : '') + LF;
        // Quantity and price aligned right
        formatted += this.formatLine(`  ${qty} @ ${price}`, total) + LF;
        
        // Add special instructions if any (but not the COMPLIMENTARY note)
        if ((item.note || item.special_instructions) && item.note !== 'COMPLIMENTARY') {
          formatted += `  Note: ${item.note || item.special_instructions}` + LF;
        }
      });
    } else {
      formatted += 'No items' + LF;
    }
    
    formatted += '-'.repeat(this.charWidth) + LF;
    
    // Financial totals
    formatted += this.formatLine('Subtotal:', this.formatCurrency(data.subtotal)) + LF;
    
    // Discounts
    if (data.totalDiscount > 0) {
      formatted += this.formatLine('Discount:', `-${this.formatCurrency(data.totalDiscount)}`) + LF;
    }
    
    // Service charge
    if (data.serviceChargeAmount > 0) {
      formatted += this.formatLine('Service Charge:', this.formatCurrency(data.serviceChargeAmount)) + LF;
    }
    
    // Tax
    if (data.tax > 0) {
      formatted += this.formatLine('Tax:', this.formatCurrency(data.tax)) + LF;
    }
    
    // Total
    formatted += this.formatLine('TOTAL:', this.formatCurrency(data.total)) + LF;
    formatted += '='.repeat(this.charWidth) + LF;
    
    // Summary
    formatted += `Total Items: ${data.items?.length || 0}` + LF;
    formatted += `Total Quantity: ${data.items?.reduce((sum, item) => sum + (item.qty || item.quantity || 1), 0) || 0}` + LF;
    formatted += '='.repeat(this.charWidth) + LF;
    
    // Special instructions
    if (data.note) {
      formatted += 'Special Instructions:' + LF;
      formatted += data.note + LF;
      formatted += '='.repeat(this.charWidth) + LF;
    }
    
    // Footer - centered
    formatted += ESC + 'a' + String.fromCharCode(1); // Center align
    formatted += '*** END OF ORDER ***' + LF;
    formatted += 'Time: ' + new Date().toLocaleTimeString("en-PH", {
      hour: "2-digit",
      minute: "2-digit",
      hour12: true,
    }) + LF;
    
    formatted += ESC + 'a' + String.fromCharCode(0); // Left align
    formatted += LF + LF + LF; // Feed paper
    
    // Printer-specific commands (no cash drawer for order slips, but cut paper)
    if (this.printerType === 'rawbt' || this.printerType === 'qz_tray') {
      // Cut paper command
      formatted += GS + 'V' + String.fromCharCode(0);
    }
    
    return formatted;
  }

  // Create a formatter instance for specific printer type
  static createFormatter(printerType = 'browser', thermalWidth = 58) {
    return new ReceiptFormatter(printerType, thermalWidth);
  }

  // Helper method to format receipt for ThermalReceiptPreview component
  static formatForBrowserPreview(receiptData, thermalWidth = 58) {
    const formatter = new ReceiptFormatter('browser', thermalWidth);
    return formatter.generateReceipt(receiptData);
  }

  // Helper method to format receipt for QZ Tray
  static formatForQZTray(receiptData, thermalWidth = 58) {
    const formatter = new ReceiptFormatter('qz_tray', thermalWidth);
    return formatter.generateReceipt(receiptData);
  }

  // Helper method to format receipt for RawBT
  static formatForRawBT(receiptData, thermalWidth = 58) {
    const formatter = new ReceiptFormatter('rawbt', thermalWidth);
    return formatter.generateReceipt(receiptData);
  }

  // Helper methods for order slips
  static formatOrderSlipForBrowserPreview(orderData, thermalWidth = 58) {
    const formatter = new ReceiptFormatter('browser', thermalWidth);
    return formatter.generateOrderSlip(orderData);
  }

  static formatOrderSlipForQZTray(orderData, thermalWidth = 58) {
    const formatter = new ReceiptFormatter('qz_tray', thermalWidth);
    return formatter.generateOrderSlip(orderData);
  }

  static formatOrderSlipForRawBT(orderData, thermalWidth = 58) {
    const formatter = new ReceiptFormatter('rawbt', thermalWidth);
    return formatter.generateOrderSlip(orderData);
  }
}

// Export default instance and class
export default ReceiptFormatter;